import type { HokmMove, HokmView } from "@bing/engine-hokm";
import { HOKM_ADVANCED_ONLINE_ROOM, HOKM_ONLINE_ROOM } from "@bing/shared";
import type { ReactNode } from "react";
import { fa } from "../i18n/fa";
import { HokmTable, type HokmSeatPlayer } from "../screens/HokmTable";
import { getHokmPublicAdvanced } from "../settings/prefs";
import { useSession } from "../store/context";
import { WinOverlay } from "./HokmGame";
import type { LobbyPlayer } from "./online-connection";
import { OnlineGame } from "./OnlineGame";

/** Online حکم — thin wrapper over the shared `OnlineGame` shell. Threads the self
 * profile (own-seat photo + name) and the lobby roster (real-user seat names) into
 * the table; the shell's `players` carry the server-resolved name, mapped onto
 * `displayName()`'s `nickname` slot so seats read app-consistently. */
/** The lobby roster as the felt and the end card both want it — ONE mapping (card-98 item 12), so a
 *  chair can never be a named person on the table and a bot label on the end card. The seat's real
 *  face: their uploaded photo, else THEIR preset (not the positional one) — the identity is already on
 *  the wire (this map used to drop it, so a friend's photo never reached the felt). */
function hokmSeatPlayers(
  players: readonly LobbyPlayer[] | undefined,
): readonly HokmSeatPlayer[] | undefined {
  return players?.map((p) => ({
    seat: p.seat,
    isBot: p.isBot,
    nickname: p.name,
    avatarId: p.user?.avatarId,
    avatarPhotoId: p.user?.avatarPhotoId,
    isOfficial: p.user?.isOfficial,
  }));
}

export function HokmOnlineGame(): ReactNode {
  const profile = useSession((s) => s.profile);
  /*
   * TWO PUBLIC ROOM TYPES, so «standard or advanced» picks the QUEUE, not a setting.
   *
   * A quick-match table has nobody to choose for the stranger who joins it, and this branch sends
   * no settings at all (the server ignores them there outright); `maxClients`/name are fixed at
   * `define()` time, so a room type IS its matchmaking queue. Somebody who tapped «اتاق عمومی»
   * must never be seated where the Hakem can call نرس, which is exactly why the two cannot share
   * one room. The server FORCES the rule from whichever room you land in — this only chooses which.
   *
   * (It used to split by MATCH LENGTH instead — `hokm3-online` / `hokm5-online`. A public table
   * offers no choices now: every one of them runs the canonical seven rounds.)
   */
  const room = getHokmPublicAdvanced() ? HOKM_ADVANCED_ONLINE_ROOM : HOKM_ONLINE_ROOM;
  return (
    <OnlineGame<HokmMove, HokmView>
      gameId="hokm"
      room={room}
      /* ⚠️ STATED, NEVER DERIVED. `OnlineGame` otherwise swaps `-online` → `-private`, which on the
         advanced room asks for a `hokm-advanced-private` the server registers nowhere — «با دوستام»
         would then break for exactly the players whose last public tap was «پیشرفته». There is ONE
         private حکم room type; a host picks the length AND the calls in «تنظیمات میز». */
      privateRoom="hokm-private"
      title={fa.hokm.title}
      renderTable={({ view, legal, send, leave, players, timer, onInviteSeat, seatNotices }) => (
        <HokmTable
          seatNotices={seatNotices}
          view={view}
          legal={legal}
          onMove={send}
          onLeave={leave}
          self={profile}
          timer={timer}
          onInviteSeat={onInviteSeat}
          // EVERY online table gets the chat toggle — OnlineGame mounts it iff `lobby?.roomId`, and
          // the server sends the room id on every lobby frame (`lobbyPayload`) — so its slot is
          // reserved from the FIRST frame, always: on a resume the state frame lands before the
          // lobby frame, and a slot that waited for it moved «حکم» 24 px (operator: «things must
          // not move around»).
          chat
          players={hokmSeatPlayers(players)}
        />
      )}
      renderWin={({
        result,
        view,
        again,
        leave,
        players,
        againDisabled,
        againHint,
        againHidden,
      }) => (
        <WinOverlay
          result={result}
          teamScores={view.teamScores}
          you={view.you}
          // card-98 item 12: the rows name the players — the SAME identity pair the felt gets
          self={profile}
          players={hokmSeatPlayers(players)}
          onAgain={again}
          againDisabled={againDisabled}
          againHint={againHint}
          againHidden={againHidden}
          onLeave={leave}
        />
      )}
    />
  );
}
